import React, { useMemo, useState, useEffect } from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(ArcElement, Tooltip, Legend);

// === Styled Components (card-friendly, responsive) ===
const ChartWrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  height: 100%;
  padding: 12px 15px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

const TitleWrapper = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const Controls = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 10px;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 6px;
`;

/* Flexible chart area that grows to fill leftover space */
const ChartArea = styled.div`
  flex: 1;
  width: 100%;
  min-height: 240px;
  position: relative;
  display: flex;
  align-items: stretch;
`;

// === Utility ===
const parseVal = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

// Current colors (outer ring)
const GREEN = "rgba(46, 125, 50, 0.85)";     // Kraj
const ORANGE = "rgba(251, 140, 0, 0.85)";    // UE
const BLUE = "rgba(25, 118, 210, 0.85)";     // Eksport

// Previous-year colors (inner ring, lighter)
const GREEN_L = "rgba(46, 125, 50, 0.30)";
const ORANGE_L = "rgba(251, 140, 0, 0.30)";
const BLUE_L = "rgba(25, 118, 210, 0.30)";

const TransactionTypeBreakdown = ({ data = [] }) => {
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("all");

  const years = useMemo(
    () => [...new Set(data.map(d => parseInt(d.Rok, 10)).filter(Boolean))].sort((a, b) => b - a),
    [data]
  );

  const months = [
    ["all", "Cały rok"],
    ["1", "Styczeń"], ["2", "Luty"], ["3", "Marzec"], ["4", "Kwiecień"],
    ["5", "Maj"], ["6", "Czerwiec"], ["7", "Lipiec"], ["8", "Sierpień"],
    ["9", "Wrzesień"], ["10", "Październik"], ["11", "Listopad"], ["12", "Grudzień"]
  ];

  useEffect(() => {
    if (years.length > 0 && selectedYear == null) {
      setSelectedYear(years[0]);
    }
  }, [years, selectedYear]);

  const { chartData, hasAnyData } = useMemo(() => {
    if (!selectedYear) {
      return { chartData: null, hasAnyData: false };
    }

    const calcBuckets = (yr, mSel) => {
      let kraj = 0, ue = 0, eksport = 0;

      data.forEach(row => {
        const y = parseInt(row.Rok, 10);
        const m = parseInt(row.Miesiac, 10);

        if (y !== yr) return;
        if (mSel !== "all" && m !== parseInt(mSel, 10)) return;

        // KRAJ = K_15 + K_17 + K_19 + K_10
        kraj += parseVal(row.K_15) + parseVal(row.K_17) + parseVal(row.K_19) + parseVal(row.K_10);
        // UE   = K_21 + K_11
        ue += parseVal(row.K_21) + parseVal(row.K_11);
        // EKSPORT = K_22
        eksport += parseVal(row.K_22);
      });

      return [kraj, ue, eksport];
    };

    const now = calcBuckets(parseInt(selectedYear, 10), selectedMonth);
    const prev = calcBuckets(parseInt(selectedYear, 10) - 1, selectedMonth);
    const hasData = now.reduce((a, b) => a + b, 0) > 0 || prev.reduce((a, b) => a + b, 0) > 0;

    const labels = ["Kraj", "UE", "Eksport"];
    const chartData = {
      labels,
      datasets: [
        // Inner ring: previous year (r-1)
        {
          label: `r-1 (${selectedMonth === "all" ? "cały rok" : `miesiąc ${selectedMonth}`})`,
          data: prev,
          backgroundColor: [GREEN_L, ORANGE_L, BLUE_L],
          borderColor: ["#2e7d32", "#fb8c00", "#1976d2"],
          borderWidth: 1,
          radius: "70%",
          cutout: "40%",
        },
        // Outer ring: current year
        {
          label: `${selectedYear} (${selectedMonth === "all" ? "cały rok" : `miesiąc ${selectedMonth}`})`,
          data: now,
          backgroundColor: [GREEN, ORANGE, BLUE],
          borderColor: ["#2e7d32", "#fb8c00", "#1976d2"],
          borderWidth: 1,
          radius: "95%",
          cutout: "40%",
        },
      ],
    };

    return { chartData, hasAnyData: hasData };
  }, [data, selectedYear, selectedMonth]);

  return (
    <ChartWrapper>
      <TitleWrapper>
        Podział sprzedaży wg pochodzenia transakcji
        <span
          style={{ display: "inline-flex", alignItems: "center" }}
          data-tooltip-id="transaction-type-tooltip"
        >
          <Info style={{ width: 18, height: 18, color: "#666", cursor: "pointer" }} />
        </span>
      </TitleWrapper>

      <InfoTooltip
        id="transaction-type-tooltip"
        place="left"
        style={{ backgroundColor: "#333", color: "#fff", fontSize: "12px", fontFamily: "Arial, sans-serif" }}
        content={
          <>
            <strong>Podział sprzedaży (Kraj / UE / Eksport)</strong><br />
            Zewnętrzny pierścień: wybrany rok. Wewnętrzny: ten sam okres rok wcześniej.
          </>
        }
      />

      <Controls>
        <FilterGroup>
          <label>Rok:</label>
          <Select
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            <option value="">Wybierz rok</option>
            {years.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </Select>
        </FilterGroup>

        <FilterGroup>
          <label>Miesiąc:</label>
          <Select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            {months.map(([val, label]) => (
              <option key={val} value={val}>{label}</option>
            ))}
          </Select>
        </FilterGroup>
      </Controls>

      {hasAnyData && chartData ? (
        <ChartArea>
          <Pie
            data={chartData}
            plugins={[ChartDataLabels]}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { position: "bottom" },
                tooltip: {
                  backgroundColor: "#333",
                  titleColor: "#fff",
                  bodyColor: "#fff",
                  callbacks: {
                    label: (ctx) => {
                      const label = ctx.label || "";
                      const val = Number(ctx.raw) || 0;
                      const dataset = ctx.dataset;
                      const total =
                        (dataset?.data || []).reduce((s, v) => s + Number(v || 0), 0);
                      const pct = total ? ((val / total) * 100).toFixed(1) : "0.0";
                      return `${ctx.dataset.label} – ${label}: ${val.toLocaleString("pl-PL")} (${pct}%)`;
                    },
                  },
                },
                datalabels: {
                  // show % only for the OUTER ring (current year)
                  display: (ctx) => ctx.datasetIndex === 1,
                  color: "#fff",
                  font: { size: 11, weight: "bold" },
                  formatter: (value, ctx) => {
                    if (ctx.datasetIndex !== 1) return "";
                    const arr = ctx.dataset.data || [];
                    const total = arr.reduce((a, b) => a + Number(b || 0), 0);
                    return total ? `${((Number(value) / total) * 100).toFixed(1)}%` : "";
                  },
                },
              },
              animation: { duration: 350 },
            }}
          />
        </ChartArea>
      ) : (
        <p style={{ textAlign: "center", marginTop: "20px", color: "#888" }}>
          Brak danych do wyświetlenia.
        </p>
      )}
    </ChartWrapper>
  );
};

export default TransactionTypeBreakdown;
